import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Container,
  Drawer,
  List,
  ListItem,
  Checkbox,
  FormControlLabel,
  Slider,
  Divider,
  TextField,
  InputAdornment,
  IconButton,
  useMediaQuery,
  useTheme,
  Chip,
  Badge,
  Link,
  CircularProgress,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import BlockIcon from '@mui/icons-material/Block';
import { useNavigate } from 'react-router-dom';

import type { BeerFilters, Beer, FilterOptions } from 'utils/types/Beer';
import { BeerService } from 'services/firebase/beerService';
import { useCart } from 'services/cart/CartContext';
import { analyticsService } from 'services/analytics/analyticsService';
import { SemAlcool, SemGluten, LowCarb } from 'assets/icons';
import Banner from 'components/Banner';
import { getActivePromotionalBanner } from 'services/firebase/promotionalBannerService';
import type { PromotionalBanner } from 'utils/types/PromotionalBanner';
import styles from './styles';

const BeerStore: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isCompactCard = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { addToCart, openCart } = useCart();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filters, setFilters] = useState<BeerFilters>({
    priceRange: { min: 0, max: 50 },
    breweries: [],
    styles: [],
    searchTerm: '',
    showOutOfStock: true, // Por padrão, mostrar produtos fora de estoque
  });

  // Estados para dados do Firebase
  const [beers, setBeers] = useState<Beer[]>([]);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    breweries: [],
    styles: [],
    priceRange: { min: 0, max: 50 },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [promotionalBanner, setPromotionalBanner] =
    useState<PromotionalBanner | null>(null);

  useEffect(() => {
    let cancelled = false;
    getActivePromotionalBanner('beer-store')
      .then(data => {
        if (!cancelled) setPromotionalBanner(data);
      })
      .catch(() => {
        if (!cancelled) setPromotionalBanner(null);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Carregar dados iniciais do Firebase
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [beersData, filterOptionsData] = await Promise.all([
          BeerService.getAllBeers(),
          BeerService.getFilterOptions(),
        ]);

        setBeers(beersData);
        setFilterOptions(filterOptionsData);

        // Atualizar faixa de preços dos filtros com os dados reais
        setFilters(prev => ({
          ...prev,
          priceRange: filterOptionsData.priceRange,
        }));

        // Track acesso à beer-store
        analyticsService.trackBeerStoreAccess('beer-store');
      } catch (err) {
        console.error('Erro ao carregar dados:', err);
        setError('Erro ao carregar cervejas. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Função para filtrar cervejas localmente
  const filteredBeers = useMemo(() => {
    return beers.filter(beer => {
      // Filtro por termo de busca
      const matchesSearch =
        !filters.searchTerm ||
        beer.name.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        beer.brewery.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        beer.style.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        beer.description
          .toLowerCase()
          .includes(filters.searchTerm.toLowerCase());

      // Filtro por preço
      const matchesPrice =
        beer.price >= filters.priceRange.min &&
        beer.price <= filters.priceRange.max;

      // Filtro por cervejaria
      const matchesBrewery =
        filters.breweries.length === 0 ||
        filters.breweries.includes(beer.brewery);

      // Filtro por estilo
      const matchesStyle =
        filters.styles.length === 0 || filters.styles.includes(beer.style);

      // Filtro por disponibilidade
      const matchesStock = filters.showOutOfStock || beer.inStock;

      return (
        matchesSearch &&
        matchesPrice &&
        matchesBrewery &&
        matchesStyle &&
        matchesStock
      );
    });
  }, [beers, filters]);

  const handleBreweryChange = (brewery: string, checked: boolean) => {
    setFilters(prev => ({
      ...prev,
      breweries: checked
        ? [...prev.breweries, brewery]
        : prev.breweries.filter(b => b !== brewery),
    }));
  };

  const handleStyleChange = (style: string, checked: boolean) => {
    setFilters(prev => ({
      ...prev,
      styles: checked
        ? [...prev.styles, style]
        : prev.styles.filter(s => s !== style),
    }));
  };

  const handleShowOutOfStockChange = (checked: boolean) => {
    setFilters(prev => ({
      ...prev,
      showOutOfStock: checked,
    }));
  };

  const handlePriceChange = (event: Event, newValue: number | number[]) => {
    const [min, max] = newValue as number[];
    setFilters(prev => ({
      ...prev,
      priceRange: { min, max },
    }));
  };

  const clearFilters = () => {
    setFilters({
      priceRange: filterOptions.priceRange,
      breweries: [],
      styles: [],
      searchTerm: '',
      showOutOfStock: true,
    });
  };

  const activeFiltersCount =
    filters.breweries.length +
    filters.styles.length +
    (filters.priceRange.min > filterOptions.priceRange.min ||
    filters.priceRange.max < filterOptions.priceRange.max
      ? 1
      : 0);

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleAddToCart = (beer: any) => {
    addToCart(beer);
    openCart();

    // Track add to cart event
    analyticsService.trackAddToCart(beer.id, beer.name, beer.price, 1);
  };

  // Componente de filtros
  const FiltersContent = () => (
    <Box sx={styles.filtersContainer}>
      <Box sx={styles.filtersHeader}>
        <Typography variant="h6" sx={styles.filtersTitle}>
          Filtros
        </Typography>
        {activeFiltersCount > 0 && (
          <Button onClick={clearFilters} size="small" sx={styles.clearButton}>
            Limpar ({activeFiltersCount})
          </Button>
        )}
      </Box>

      <Divider sx={styles.divider} />

      {/* Filtro de Preço */}
      <Box sx={styles.filterSection}>
        <Typography variant="subtitle1" sx={styles.filterSectionTitle}>
          Preço
        </Typography>
        <Slider
          value={[filters.priceRange.min, filters.priceRange.max]}
          onChange={handlePriceChange}
          valueLabelDisplay="auto"
          min={filterOptions.priceRange.min}
          max={filterOptions.priceRange.max}
          step={0.5}
          valueLabelFormat={value => `R$ ${value.toFixed(2)}`}
          sx={styles.priceSlider}
        />
        <Box sx={styles.priceRange}>
          <Typography variant="body2">
            R$ {filters.priceRange.min.toFixed(2)} - R${' '}
            {filters.priceRange.max.toFixed(2)}
          </Typography>
        </Box>
      </Box>

      <Divider sx={styles.divider} />

      {/* Filtro de Cervejaria */}
      <Box sx={styles.filterSection}>
        <Typography variant="subtitle1" sx={styles.filterSectionTitle}>
          Cervejaria
        </Typography>
        <List dense sx={styles.filterList}>
          {filterOptions.breweries.map(brewery => (
            <ListItem key={brewery} sx={styles.filterListItem}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.breweries.includes(brewery)}
                    onChange={e =>
                      handleBreweryChange(brewery, e.target.checked)
                    }
                    size="small"
                    sx={styles.filterCheckbox}
                  />
                }
                label={
                  <Typography variant="body2" sx={styles.filterLabel}>
                    {brewery}
                  </Typography>
                }
                sx={styles.formControlLabel}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={styles.divider} />

      {/* Filtro de Estilo */}
      <Box sx={styles.filterSection}>
        <Typography variant="subtitle1" sx={styles.filterSectionTitle}>
          Estilo
        </Typography>
        <List dense sx={styles.filterList}>
          {filterOptions.styles.map(style => (
            <ListItem key={style} sx={styles.filterListItem}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.styles.includes(style)}
                    onChange={e => handleStyleChange(style, e.target.checked)}
                    size="small"
                    sx={styles.filterCheckbox}
                  />
                }
                label={
                  <Typography variant="body2" sx={styles.filterLabel}>
                    {style}
                  </Typography>
                }
                sx={styles.formControlLabel}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={styles.divider} />

      {/* Filtro de Disponibilidade */}
      <Box sx={styles.filterSection}>
        <Typography variant="subtitle1" sx={styles.filterSectionTitle}>
          Disponibilidade
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={filters.showOutOfStock}
              onChange={e => handleShowOutOfStockChange(e.target.checked)}
              size="small"
              sx={styles.filterCheckbox}
            />
          }
          label={
            <Typography variant="body2" sx={styles.filterLabel}>
              Mostrar produtos esgotados
            </Typography>
          }
          sx={styles.formControlLabel}
        />
      </Box>
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={styles.container}>
      {promotionalBanner?.image && (
        <Banner
          imageSrc={promotionalBanner.image}
          storageKey={promotionalBanner.storageKey}
          alt={promotionalBanner.alt}
          ctaText={promotionalBanner.ctaText}
          ctaUrl={promotionalBanner.ctaUrl}
        />
      )}
      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress size={60} />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          <Button
            size="small"
            onClick={() => window.location.reload()}
            sx={{ ml: 2 }}
          >
            Tentar novamente
          </Button>
        </Alert>
      )}

      {/* Conteúdo principal - só mostra se não está loading e não há erro */}
      {!loading && !error && (
        <>
          {/* Barra de busca e filtros móveis */}
          <Box sx={styles.searchAndFilterBar}>
            <TextField
              fullWidth
              placeholder="Buscar por nome, cervejaria ou estilo..."
              value={filters.searchTerm}
              onChange={e =>
                setFilters(prev => ({ ...prev, searchTerm: e.target.value }))
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={styles.searchField}
            />

            {isMobile && (
              <IconButton onClick={toggleDrawer} sx={styles.mobileFilterButton}>
                <Badge badgeContent={activeFiltersCount} color="primary">
                  <FilterListIcon />
                </Badge>
              </IconButton>
            )}
          </Box>

          {/* Pills de estilos */}
          {filterOptions.styles.length > 0 && (
            <Box sx={styles.stylePills}>
              <Chip
                label="Todos"
                size="small"
                clickable
                onClick={() =>
                  setFilters(prev => ({ ...prev, styles: [] }))
                }
                sx={
                  filters.styles.length === 0
                    ? styles.stylePillActive
                    : styles.stylePill
                }
              />
              {filterOptions.styles.map(style => {
                const isSelected = filters.styles.includes(style);
                return (
                  <Chip
                    key={style}
                    label={style}
                    size="small"
                    clickable
                    onClick={() => handleStyleChange(style, !isSelected)}
                    sx={isSelected ? styles.stylePillActive : styles.stylePill}
                  />
                );
              })}
            </Box>
          )}

          {/* Filtros ativos de cervejaria */}
          {filters.breweries.length > 0 && (
            <Box sx={styles.activeFilters}>
              {filters.breweries.map(brewery => (
                <Chip
                  key={brewery}
                  label={brewery}
                  onDelete={() => handleBreweryChange(brewery, false)}
                  size="small"
                  sx={styles.filterChip}
                />
              ))}
            </Box>
          )}

          {/* Layout principal */}
          <Grid container spacing={3}>
            {/* Sidebar de filtros (desktop) */}
            {!isMobile && (
              <Grid item md={3}>
                <Box sx={styles.sidebarFilters}>
                  <FiltersContent />
                </Box>
              </Grid>
            )}

            {/* Lista de produtos */}
            <Grid item xs={12} md={isMobile ? 12 : 9}>
              {/* Contador de resultados */}
              <Box sx={styles.resultsHeader}>
                <Typography variant="body1" sx={styles.resultsCount}>
                  {filteredBeers.length} produto
                  {filteredBeers.length !== 1 ? 's' : ''} encontrado
                  {filteredBeers.length !== 1 ? 's' : ''}
                </Typography>
              </Box>

              {/* Grid de produtos */}
              <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
                {filteredBeers.map(beer => (
                  <Grid item xs={6} sm={6} md={4} lg={3} key={beer.id}>
                    <Card
                      sx={styles.productCard}
                      onClick={() => navigate(`/beer-store/beer/${beer.id}`)}
                    >
                      {/* Selos Sem Álcool / Sem Glúten */}
                      {(beer.semAlcool || beer.semGluten) && (
                        <Box sx={styles.stampsBadge}>
                          {beer.semAlcool && (
                            <Box
                              component="img"
                              src={SemAlcool}
                              alt="Sem álcool"
                              sx={styles.stampIcon}
                            />
                          )}
                          {beer.semGluten && (
                            <Box
                              component="img"
                              src={SemGluten}
                              alt="Sem glúten"
                              sx={styles.stampIcon}
                            />
                          )}
                        </Box>
                      )}

                      {/* Selo LowCarb + Badge de desconto / Fora de Estoque (canto superior direito) */}
                      {(beer.lowCarb ||
                        (beer.originalPrice && beer.inStock) ||
                        !beer.inStock) && (
                        <Box sx={styles.stampsBadgeRight}>
                          {!beer.inStock && (
                            <Box sx={styles.outOfStockBadgeInColumn}>
                              <BlockIcon sx={styles.outOfStockIcon} />
                              <Typography sx={styles.outOfStockText}>
                                ESGOTADO
                              </Typography>
                            </Box>
                          )}
                          {beer.lowCarb && (
                            <Box
                              component="img"
                              src={LowCarb}
                              alt="Low Carb"
                              sx={styles.stampIcon}
                            />
                          )}
                          {beer.originalPrice && beer.inStock && (
                            <Box sx={styles.discountBadgeInColumn}>
                              <LocalOfferIcon sx={styles.discountIcon} />
                              <Typography
                                variant="caption"
                                sx={styles.discountText}
                              >
                                -
                                {Math.round(
                                  ((beer.originalPrice - beer.price) /
                                    beer.originalPrice) *
                                    100,
                                )}
                                %
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}

                      <CardMedia
                        component="img"
                        image={beer.image}
                        alt={beer.name}
                        sx={styles.productImage}
                      />

                      <CardContent sx={styles.productContent}>
                        <Typography
                          variant="h6"
                          sx={styles.productName}
                          noWrap
                          title={beer.name}
                        >
                          {beer.name}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={styles.productBrewery}
                        >
                          {beer.brewery}
                        </Typography>

                        <Typography variant="body2" sx={styles.productStyle}>
                          {beer.style} • {beer.abv}% ABV • {beer.volume}ml
                        </Typography>

                        <Box sx={styles.priceContainer}>
                          {beer.originalPrice && (
                            <Typography
                              variant="body2"
                              sx={styles.originalPrice}
                            >
                              R$ {beer.originalPrice.toFixed(2)}
                            </Typography>
                          )}
                          <Typography variant="h6" sx={styles.currentPrice}>
                            R$ {beer.price.toFixed(2)}
                          </Typography>
                        </Box>

                        {/* Seção de Assinante */}
                        <Box sx={styles.subscriberSection}>
                          <Typography
                            variant="caption"
                            sx={styles.subscriberLabel}
                          >
                            Assinante
                          </Typography>
                          <Typography variant="h6" sx={styles.subscriberPrice}>
                            R$ {beer.subscriberPrice.toFixed(2)}
                          </Typography>
                          <Link
                            component="button"
                            onClick={e => {
                              e.stopPropagation();
                              navigate('/');
                            }}
                            sx={styles.subscribeLink}
                            underline="none"
                          >
                            ASSINE AGORA
                          </Link>
                        </Box>

                        <Button
                          variant="contained"
                          fullWidth
                          disabled={!beer.inStock}
                          startIcon={
                            isCompactCard ? undefined : <AddShoppingCartIcon />
                          }
                          onClick={e => {
                            e.stopPropagation();
                            beer.inStock && handleAddToCart(beer);
                          }}
                          sx={styles.addToCartButton}
                        >
                          {isCompactCard ? 'Adicionar' : 'Adicionar ao Carrinho'}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Mensagem quando não há resultados */}
              {filteredBeers.length === 0 && (
                <Box sx={styles.noResults}>
                  <Typography variant="h6" sx={styles.noResultsTitle}>
                    Nenhum produto encontrado
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Tente ajustar os filtros ou termo de busca para encontrar o
                    que procura.
                  </Typography>
                  <Button
                    onClick={clearFilters}
                    variant="outlined"
                    sx={styles.clearFiltersButton}
                  >
                    Limpar Filtros
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>

          {/* Drawer para filtros móveis */}
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={toggleDrawer}
            sx={styles.mobileDrawer}
          >
            <Box sx={styles.drawerContent}>
              <FiltersContent />
            </Box>
          </Drawer>
        </>
      )}
    </Container>
  );
};

export default BeerStore;
